import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getTemplateDetails, updateTemplate, publishTemplate, deleteUploadedTemplateFile } from '../../../service/template';
import CustomToast from '../../Toast';
import TemplateHeader from '../../layouts/templateHeader';
import apiFunctions from '../../../service/axiosConfig/apiFunctions';
import { baseUrl } from '../../../constants';
import Loader from '../../layouts/loader';

const EditTemplate = () => {
    const thumbnailInputRef = useRef(null);
    const templateFilesInputRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [title, setTitle] = useState('');
    const [role, setRole] = useState('');
    const [category, setCategory] = useState('');
    const [lang, setLang] = useState('');
    const [stateValue, setStateValue] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [templateFiles, setTemplateFiles] = useState([]);
    const [templatePreview, setTemplatePreview] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [uploadedThumbnailUrl, setUploadedThumbnailUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [loader, setLoader] = useState(false);
    const [status, setStatus] = useState('draft');
    const { id } = useParams();
    const templateId = id;

    useEffect(() => {
        if (templateId) fetchTemplate(templateId);
    }, [templateId]);

    const fetchTemplate = async (templateId) => {
        try {
            const res = await getTemplateDetails(templateId);
            const data = res.data;
            setTitle(data.title || "");
            setRole(data.role || "");
            setCategory(data.category || "");
            setLang(data.lang || "");
            setStateValue(data.state || "");
            setThumbnailPreview(data?.thumbnail || null);
            setUploadedThumbnailUrl(data?.thumbnail || null);
            setTemplateFiles(data?.upload_url || []);
            
            // Convert URL strings to file objects for existing templates
            const existingFiles = (data?.upload_url || []).map((url, index) => {
                if (typeof url === 'string') {
                    // Extract filename from URL with better parsing
                    const urlParts = url.split('/');
                    const lastPart = urlParts[urlParts.length - 1];
                    // Remove query parameters and decode URI components
                    const decodedName = decodeURIComponent(lastPart.split('?')[0]);
                    // Remove timestamp prefixes (like "1640995200000-filename.pdf" or similar patterns)
                    const cleanName = decodedName.replace(/^\d{10,}-/, '').replace(/^[a-f0-9]{32}-/, '');
                    
                    return {
                        url: url,
                        name: cleanName || `Document_${index + 1}`,
                        originalName: cleanName
                    };
                }
                return url; // If it's already an object
            });
            
            setUploadedFiles(existingFiles);
            setStatus(data.status || 'draft'); 
        } catch (err) {
            console.error(err);
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setThumbnailFile(file);
            setThumbnailPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setUploadedThumbnailUrl(null);
        setPreviewUrl(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
        CustomToast.success("Thumbnail removed successfully!");
    };

    const handleUploadThumbnail = async () => {
        if (!thumbnailFile) {
            CustomToast.error("Please select a thumbnail file first.");
            return;
        }

        try {
            const response = await apiFunctions.uploadDocFiles(`${baseUrl}uploadFiles`, thumbnailFile);
            const uploadedUrl = response?.data?.urls[0];
            setUploadedThumbnailUrl(uploadedUrl);
            setPreviewUrl(uploadedUrl);
            setThumbnailPreview(uploadedUrl);
            setThumbnailFile(null);
            if (thumbnailInputRef.current) {
                thumbnailInputRef.current.value = '';
            }
            CustomToast.success("Thumbnail uploaded successfully!");
        } catch (error) {
            CustomToast.error("Thumbnail upload failed.");
        }
    };

    const handleTemplateChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const validFiles = files.filter(file => file.size <= 50 * 1024 * 1024);
        const invalidFiles = files.filter(file => file.size > 50 * 1024 * 1024);

        if (invalidFiles.length > 0) {
            alert(`${invalidFiles.length} file(s) exceeded 50MB limit and were skipped.`);
        }

        setTemplateFiles(validFiles);

        const imageFile = validFiles.find(file => file.type.startsWith("image/"));
        if (imageFile) {
            setTemplatePreview(URL.createObjectURL(imageFile));
        } else {
            setTemplatePreview(null);
        }
    };

    const handleRemoveUploadedFile = async (fileId) => {
        setLoader(true);
        try {
            await deleteUploadedTemplateFile(templateId, fileId);
            const updatedFiles = uploadedFiles.filter(file => file._id !== fileId);
            setUploadedFiles(updatedFiles);
            setTemplateFiles(updatedFiles);
            setLoader(false);
            CustomToast.success("File deleted successfully!");
        } catch (error) {
            setLoader(false);
            CustomToast.error("Failed to delete the file.");
        }
    };

    const handleUploadTemplate = async () => {
        if (!templateFiles.length) {
            alert("No documents selected.");
            return;
        }
        setIsUploading(true);
        try {
            const response = await apiFunctions.uploadFiles(`${baseUrl}uploadFiles`, templateFiles);
            const urls = response?.data?.urls || [];
            
            // Create file objects with both URL and original file name
            const fileObjects = urls.map((url, index) => ({
                url: url,
                name: templateFiles[index]?.name || `Document_${index + 1}`,
                originalName: templateFiles[index]?.name
            }));
            
            setUploadedFiles(prev => [...prev, ...fileObjects]);
            setTemplateFiles([]);
            setTemplatePreview(null);
            if (templateFilesInputRef.current) {
                templateFilesInputRef.current.value = '';
            }
            CustomToast.success("Documents uploaded successfully.");
        } catch (error) {
            console.error("Upload error:", error);
            CustomToast.error("Error uploading documents.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSaveOrPublish = async (statusType) => {
        setIsSaving(true);
        setIsPublishing(statusType === "published");

        // Convert file objects to URLs for the API payload
        const fileUrls = uploadedFiles.map(file => 
            typeof file === 'string' ? file : file.url
        );

        const payload = {
            title,
            role,
            category,
            status: statusType,
            is_publish: statusType === "published" ? true : false,
            publish_date:
                statusType === "published" ? new Date().toISOString() : null,
            thumbnail: uploadedThumbnailUrl,
            upload_url: fileUrls
        };

        payload.lang = (lang && lang.trim() !== '') ? lang : null;
        payload.state = (stateValue && stateValue.trim() !== '') ? stateValue : null;

        try {
            const res = await updateTemplate(id, payload);

            if (res?.data?.status) {
                setStatus(statusType); 
                setTimeout(() => {
                    window.location.reload(true);
                }, 1000);
            } else {
                console.error("Error updating template:", res?.message);
                handleValidationMessages(res?.message);
            }

        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
            setIsPublishing(false);
        }
    };

    const handleValidationMessages = (message) => {
        if (!message) {
            CustomToast.error('Unexpected error occurred.');
            return;
        }

        if (message.includes('"title" is not allowed to be empty')) {
            CustomToast.error('Title is required.');
        } else if (message.includes('"role" is not allowed to be empty')) {
            CustomToast.error('Role is required.');
        } else if (message.includes('"category" is not allowed to be empty')) {
            CustomToast.error('Category is required.');
        } else if (message.includes('"lang" is not allowed to be empty')) {
            CustomToast.error('Language is required.');
        } else if (message.includes('"state" is not allowed to be empty')) {
            CustomToast.error('State is required.');
        } else if (message.includes('Thumbnail must be a string')) {
            CustomToast.error('Thumbnail is required');
        } else if (message.includes('least one file must be uploaded')) {
            CustomToast.error('At least one template file must be uploaded');
        } else {
            CustomToast.error('Error occurred while saving the Template.');
        }
    };

    const handleTogglePublishStatus = async () => {
        setIsPublishing(true);
        try {
            const newStatus = status === 'published' ? 'draft' : 'published';
            const response = await handleUpdateTemplateStatus(templateId, newStatus);
            if (response?.data?.status) {
                setStatus(newStatus);
                const actionMessage = newStatus === 'published' ? 'published' : 'moved to draft';
                CustomToast.success(`Template ${actionMessage} successfully`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsPublishing(false);
        }
    };

    const handleUpdateTemplateStatus = async (templateId, newStatus) => {
        const data = {
            is_publish: newStatus === 'published',
            publish_date: new Date().toISOString().split('T')[0],
            status: newStatus
        };
        try {
            const response = await publishTemplate(templateId, data);
            return response;
        } catch (error) {
            CustomToast.error(`Error while updating Template status`);
            return error;
        }
    };

    return (
        <>
            <TemplateHeader
                isEdit={true}
                isSaving={isSaving}
                isPublishing={isPublishing}
                onSaveDraft={() => handleSaveOrPublish("draft")}
                onPublish={handleTogglePublishStatus}
                isSaveDisabled={false}
                currentStatus={status}
            />
            {/* Sidebar */}
            <aside className="course_details_sidebar sidebar px-4 pt-5" id="admin-dash-nav">
                <div className='d-flex flex-column justify-content-between p-3 course_details'>
                    {/* Thumbnail Upload */}
                    <div className='row'>
                        <h5 className='font-20 mb-4'>Template Thumbnail</h5>
                        <div className='d-flex justify-content-between align-items-start position-relative'>
                            <input
                                ref={thumbnailInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                style={{ maxWidth: '240px' }}
                            />
                            {(thumbnailPreview || thumbnailFile) && (
                                <button type='button' onClick={handleRemoveThumbnail} className='position-absolute' style={{ right: '15px', padding: '0px 5px 3px', width: '30px' }}>
                                    <img src="/images/delete.png" alt="remove" className="remove-pic" />
                                </button>
                            )}
                        </div>
                        {thumbnailFile && (
                            <button
                                type='button'
                                className='mt-4 w-75'
                                onClick={handleUploadThumbnail}
                                disabled={!thumbnailFile}
                            >
                                Upload Image
                            </button>
                        )}
                        {thumbnailPreview ? (
                            <img src={thumbnailPreview} alt="Preview" className='my-4' style={{ maxWidth: '100%', maxHeight: '200px' }} />
                        ) : thumbnailFile ? (
                            <img src={URL.createObjectURL(thumbnailFile)} alt="Preview" className='my-4' style={{ maxWidth: '100%', maxHeight: '200px' }} />
                        ) : (
                            <img src="/images/image.png" alt="placeholder" className='my-4' />
                        )}
                    </div>

                    <div className='row'>
                        <h5 className='font-20 mb-4'>Upload Templates</h5>
                        <input
                            ref={templateFilesInputRef}
                            type="file"
                            multiple
                            onChange={handleTemplateChange}
                            style={{ maxWidth: '240px' }}
                        />

                        {templateFiles?.length > 0 && (
                            <ul className='mt-3'>
                                {templateFiles?.map((file, idx) => (
                                    <li key={idx}>{file.name}</li>
                                ))}
                            </ul>
                        )}

                        {loader && <Loader />}

                        {templatePreview && (
                            <div className='my-4'>
                                <h5>Template Preview</h5>
                                {templatePreview.endsWith('.pdf') ? (
                                    <iframe src={templatePreview} title="Template Preview" style={{ width: '100%', height: '500px' }} />
                                ) : (
                                    <img src={templatePreview} alt="Template Preview" style={{ maxWidth: '100%', maxHeight: '500px' }} />
                                )}
                            </div>
                        )}

                        {/* Uploaded Files */}
                        <h6 className='my-4'>Uploaded Files:</h6>
                        {uploadedFiles?.length > 0 && (
                            <>
                                {uploadedFiles.map((file, index) => {
                                    // Handle both string URLs and file objects
                                    const fileUrl = typeof file === 'string' ? file : file?.url;
                                    
                                    // Extract file name from URL or use stored name
                                    let fileName;
                                    if (file?.name || file?.originalName) {
                                        fileName = file.name || file.originalName;
                                    } else if (fileUrl) {
                                        // Extract filename from URL
                                        const urlParts = fileUrl.split('/');
                                        const lastPart = urlParts[urlParts.length - 1];
                                        // Remove query parameters and decode URI components
                                        const decodedName = decodeURIComponent(lastPart.split('?')[0]);
                                        // Remove timestamp prefixes (like "1640995200000-filename.pdf")
                                        const cleanName = decodedName.replace(/^\d{10,}-/, '').replace(/^[a-f0-9]{32}-/, '');
                                        fileName = cleanName || `Document_${index + 1}`;
                                    } else {
                                        fileName = `Document_${index + 1}`;
                                    }

                                    // Determine file type and icon
                                    const getFileTypeAndIcon = (url, name) => {
                                        const fileExtension = url?.split('.').pop()?.toLowerCase() || name?.split('.').pop()?.toLowerCase();

                                        if (fileExtension === 'pdf') {
                                            return { type: 'PDF', icon: '📄', color: '#757575' };
                                        } else if (['doc', 'docx'].includes(fileExtension)) {
                                            return { type: 'Word', icon: '📝', color: '#757575' };
                                        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension)) {
                                            return { type: 'Image', icon: '🖼️', color: '#757575' };
                                        } else if (['ppt', 'pptx'].includes(fileExtension)) {
                                            return { type: 'PowerPoint', icon: '📊', color: '#757575' };
                                        } else if (['xls', 'xlsx'].includes(fileExtension)) {
                                            return { type: 'Excel', icon: '📈', color: '#757575' };
                                        } else {
                                            return { type: 'Document', icon: '📄', color: '#757575' };
                                        }
                                    };

                                    const fileInfo = getFileTypeAndIcon(fileUrl, fileName);

                                    return (
                                        <div key={file._id || index} className="position-relative my-2 p-2 border rounded">
                                            <div className="d-flex align-items-center">
                                                <span style={{ fontSize: '20px', marginRight: '10px' }}>
                                                    {fileInfo.icon}
                                                </span>
                                                <div className="flex-grow-1">
                                                    <div>
                                                        <strong style={{ color: '#333', fontSize: '14px' }}>
                                                            {fileName}
                                                        </strong>
                                                    </div>
                                                    <small className="text-muted">{fileInfo.type} Document</small>
                                                    <div>
                                                        <a
                                                            href={fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none small"
                                                            style={{ color: fileInfo.color }}
                                                        >
                                                            📎 Open File
                                                        </a>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveUploadedFile(file._id)}
                                                    className="btn btn-sm btn-outline ms-2"
                                                    style={{ padding: '2px 8px' }}
                                                    title="Remove file"
                                                >
                                                    <img src="/images/delete.png" alt="remove" className="remove-pic" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}

                        {templateFiles?.length > 0 && (
                            <button
                                type='button'
                                className='mt-4 w-75'
                                onClick={handleUploadTemplate}
                                disabled={isUploading}
                            >
                                {isUploading ? "Uploading..." : "Upload Documents"}
                            </button>
                        )}
                    </div>

                </div>
            </aside>

            {/* Main Content */}
            <div className='course_main'>
                <div className="container-fluid">
                    <form onSubmit={handleSaveOrPublish}>
                        {/* Title */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Template Title</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>

                        {/* Role */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Role</label>
                            <select
                                className="form-select"
                                value={role}
                                onChange={(e) => {
                                    setRole(e.target.value);
                                    setCategory('');
                                }}
                            >
                                <option value="">Select Role</option>
                                <option value="admin">Org Admin</option>
                                <option value="ic">IC User</option>
                            </select>
                        </div>

                        {/* Category */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Category</label>
                            <select
                                className="form-select"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                <option value="">Select Category</option>

                                {role === 'ic' ?
                                    <>
                                        <option value="filing">Filing</option>
                                        <option value="inquiry">Inquiry</option>
                                        <option value="reports">Reports</option>
                                    </>
                                    : <><option value="policy">Policy</option>
                                        <option value="poster">Poster</option>
                                        <option value="ic_template">IC Template</option>
                                        <option value="email_campaign">Email Campaign</option>
                                        <option value="annual_report">Annual Report</option> </>}
                            </select>
                        </div>

                        {/* Language */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Language</label>
                            <select
                                className="form-select"
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                            >
                                <option value="">Select Language</option>
                                <option value="english">English</option>
                                <option value="hindi">Hindi</option>
                                <option value="marathi">Marathi</option>
                            </select>
                        </div>

                        {/* State */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">State</label>
                            <select
                                className="form-select"
                                value={stateValue}
                                onChange={(e) => setStateValue(e.target.value)}
                            >
                                <option value="">Select State</option>
                                <option value="maharashtra">Maharashtra</option>
                                <option value="karnataka">Karnataka</option>
                                <option value="tamil nadu">Tamil Nadu</option>
                                <option value="delhi">Delhi</option>
                                <option value="uttar pradesh">Uttar Pradesh</option>
                                <option value="gujarat">Gujarat</option>
                                <option value="rajasthan">Rajasthan</option>
                                <option value="kerala">Kerala</option>
                                <option value="telangana">Telangana</option>
                                <option value="punjab">Punjab</option>
                            </select>
                        </div>

                    </form>
                </div>
            </div>
        </>
    );
};

export default EditTemplate;




